import { useEffect, useState, useMemo, useRef } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import api from "@/lib/api";
import { LOGO_URL } from "@/lib/brand";

export default function PublicQuestionnaire() {
  const { token } = useParams();
  const [meta, setMeta] = useState(null);
  const [error, setError] = useState("");
  const [employee, setEmployee] = useState({ employee_name: "", employee_matricula: "" });
  const [answers, setAnswers] = useState({});  // { q1: 3, q2: 5, ... }
  const [done, setDone] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [currentDim, setCurrentDim] = useState(0);
  const [consent, setConsent] = useState(false);
  const startedAt = useRef(Date.now());
  const consentRef = useRef(null);
  // Personalised invites carry ?i=<invite_id> so we can record opened/responded back.
  const inviteId = useMemo(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get("i") || null;
  }, []);

  useEffect(() => {
    let mounted = true;
    api.get(`/public/assessments/${token}`)
      .then((r) => { if (mounted) { setMeta(r.data); startedAt.current = Date.now(); } })
      .catch((e) => { if (mounted) setError(e.response?.data?.detail || "Avaliação não encontrada"); });
    return () => { mounted = false; };
  }, [token]);

  // Fire-and-forget: tell the backend this invite link was opened. Failures
  // here never block the questionnaire (e.g. ad-block, network errors).
  useEffect(() => {
    if (!inviteId) return;
    api.post(`/public/invites/${inviteId}/opened`).catch(() => {});
  }, [inviteId]);

  const groupedQuestions = useMemo(() => {
    if (!meta) return [];
    const map = {};
    meta.questions.forEach((q) => {
      if (!map[q.dimensao]) map[q.dimensao] = [];
      map[q.dimensao].push(q);
    });
    return meta.dimensions.map((d) => ({ dim: d, questions: map[d] || [] }));
  }, [meta]);

  const answeredCount = Object.keys(answers).length;
  const totalQuestions = meta?.questions.length || 0;
  const progress = totalQuestions ? Math.round((answeredCount / totalQuestions) * 100) : 0;

  const submit = async () => {
    if (!consent) {
      // Instead of just rejecting silently, scroll to the consent block and
      // highlight it. The disabled-button state below also routes here so
      // mobile users that tap "Enviar" without scrolling get a clear hint.
      toast.error("Marque o termo de consentimento LGPD abaixo para concluir o envio.");
      consentRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });
      return;
    }
    if (answeredCount < totalQuestions) {
      if (!window.confirm(`Você respondeu ${answeredCount} de ${totalQuestions} perguntas. Enviar mesmo assim?`)) return;
    }
    setSubmitting(true);
    try {
      const duration_seconds = Math.round((Date.now() - startedAt.current) / 1000);
      await api.post("/public/responses", {
        assessment_id: meta.id,
        public_token: token,
        employee_name: employee.employee_name,
        employee_matricula: employee.employee_matricula,
        answers: Object.entries(answers).map(([qid, v]) => ({ question_id: qid, value: Number(v) })),
        duration_seconds,
        consent_lgpd: true,
        invite_id: inviteId,
      });
      setDone(true);
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (error) {
    return <div className="min-h-screen flex flex-col items-center justify-center p-6" style={{ background: "var(--so-bg)" }}>
      <i className="fa-solid fa-circle-exclamation text-5xl text-red-500 mb-4"></i>
      <h1 className="font-display text-3xl font-extrabold text-slate-900 text-center">{error}</h1>
      <p className="text-slate-600 mt-2 text-center">Verifique o link com seu gestor.</p>
    </div>;
  }
  if (!meta) return <div className="min-h-screen flex items-center justify-center text-slate-500"><i className="fa-solid fa-circle-notch fa-spin mr-2"></i> Carregando…</div>;

  if (done) {
    return <div className="min-h-screen flex flex-col items-center justify-center p-6 text-center" style={{ background: "var(--so-bg)" }}>
      <img src={LOGO_URL} alt="Sollute Ocupacional" className="h-16 w-auto mb-6" />
      <div
        className="w-24 h-24 flex items-center justify-center mb-6 rounded-full shadow-xl so-fade-in"
        style={{ background: "var(--so-gradient)", boxShadow: "0 20px 40px -10px var(--so-primary-glow)" }}
      >
        <i className="fa-solid fa-check text-white text-4xl"></i>
      </div>
      <h1 className="font-display text-5xl font-extrabold text-slate-900 mb-3 tracking-tight">Obrigado!</h1>
      <p className="text-slate-600 max-w-md mb-6 leading-relaxed">
        Sua resposta foi enviada com segurança e de forma anônima. As informações serão utilizadas apenas para fins de gestão de riscos psicossociais conforme a NR-01.
      </p>
      <div className="text-xs text-slate-500 font-mono">{meta.empresa}</div>
    </div>;
  }

  const currentGroup = groupedQuestions[currentDim];
  const isLast = currentDim >= groupedQuestions.length - 1;

  return (
    <div className="min-h-screen pb-24 sm:pb-12" style={{ background: "var(--so-bg)" }}>
      {/* Progress header */}
      <div className="so-topbar">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
          <div className="flex items-center justify-between mb-2.5 gap-3">
            <div className="flex items-center gap-3 min-w-0">
              <img src={LOGO_URL} alt="Sollute Ocupacional" className="h-8 sm:h-10 w-auto shrink-0" />
              <div className="min-w-0">
                <div className="overline text-[9px] sm:text-[10px]" style={{ color: "var(--so-primary)" }}>Questionário NR-01</div>
                <div className="text-xs sm:text-sm text-slate-600 mt-0.5 truncate">{meta.empresa}</div>
              </div>
            </div>
            <div className="text-right shrink-0">
              <div className="kpi-number text-xl sm:text-2xl" style={{ color: "var(--so-primary)" }}>
                {answeredCount}<span className="text-slate-300">/{totalQuestions}</span>
              </div>
              <div className="text-[10px] sm:text-xs text-slate-500">respondidas</div>
            </div>
          </div>
          <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
            <div
              className="h-full transition-all duration-500"
              style={{ width: `${progress}%`, background: "var(--so-gradient)" }}
            ></div>
          </div>
        </div>
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6 sm:py-10">
        {/* Identification */}
        {currentDim === 0 && (
          <div className="card mb-6 sm:mb-8">
            <div className="overline mb-3">Identificação (opcional)</div>
            <p className="text-sm text-slate-600 mb-4">
              Você pode deixar em branco para preservar o anonimato. Os campos abaixo são opcionais.
            </p>
            <div className="grid sm:grid-cols-2 gap-4">
              <div>
                <label className="label">Nome</label>
                <input value={employee.employee_name}
                       onChange={(e) => setEmployee({ ...employee, employee_name: e.target.value })}
                       className="input" data-testid="public-name-input" />
              </div>
              <div>
                <label className="label">Matrícula</label>
                <input value={employee.employee_matricula}
                       onChange={(e) => setEmployee({ ...employee, employee_matricula: e.target.value })}
                       className="input" data-testid="public-matricula-input" />
              </div>
            </div>
          </div>
        )}

        {/* Dimension header */}
        <div className="mb-5 sm:mb-6">
          <div className="overline mb-2">Dimensão {currentDim + 1} de {groupedQuestions.length}</div>
          <h2 className="font-display text-xl sm:text-2xl lg:text-3xl font-extrabold tracking-tight text-slate-900">{currentGroup.dim}</h2>
        </div>

        {/* Last-step hint banner — guides the user to scroll down for LGPD + Send */}
        {isLast && (
          <div className="card mb-5 sm:mb-6 border-amber-300 bg-amber-50" data-testid="public-last-step-hint">
            <div className="flex items-start gap-3">
              <i className="fa-solid fa-circle-info text-amber-700 text-lg mt-0.5 shrink-0"></i>
              <div className="text-xs sm:text-sm text-amber-900 leading-relaxed">
                <strong>Última etapa!</strong> Após responder as perguntas abaixo,
                role até o final desta página, <strong>marque o termo LGPD</strong>
                e clique em <strong>&quot;Enviar&quot;</strong> para concluir.
              </div>
            </div>
          </div>
        )}

        {/* Questions */}
        <div className="space-y-4 sm:space-y-6">
          {currentGroup.questions.map((q) => (
            <div key={q.id} className="card" data-testid={`public-question-${q.id}`}>
              <div className="flex items-start gap-3 mb-3 sm:mb-4">
                <span className="font-mono text-[10px] sm:text-xs text-slate-400 mt-1 shrink-0">#{q.numero}</span>
                <p className="font-semibold text-slate-900 flex-1 text-sm sm:text-base leading-snug">{q.pergunta}</p>
              </div>
              <div className="space-y-2">
                {meta.options.map((opt) => {
                  const sel = answers[q.id] === opt.value;
                  return (
                    <label key={opt.value} className={`likert-option ${sel ? "selected" : ""}`}
                           data-testid={`public-${q.id}-opt-${opt.value}`}>
                      <input type="radio" name={q.id} value={opt.value}
                             checked={sel}
                             onChange={() => setAnswers({ ...answers, [q.id]: opt.value })} />
                      <span className="text-sm font-medium">{opt.label}</span>
                    </label>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* LGPD consent — only on last dimension */}
        {isLast && (
          <div ref={consentRef} className={`card mt-6 transition-colors ${consent ? "border-emerald-200 bg-emerald-50" : "border-amber-400 bg-amber-50 ring-2 ring-amber-200"}`} data-testid="public-lgpd-block">
            <div className="overline text-emerald-900 mb-2">
              <i className="fa-solid fa-shield-halved mr-1"></i> Consentimento LGPD {!consent && <span className="text-amber-700">(obrigatório)</span>}
            </div>
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox"
                     checked={consent}
                     onChange={(e) => setConsent(e.target.checked)}
                     className="mt-1 h-5 w-5 accent-emerald-900"
                     data-testid="public-consent-checkbox" />
              <span className="text-xs sm:text-sm text-slate-700 text-justify">
                Estou ciente de que as informações fornecidas serão tratadas de forma
                <strong> anônima e agregada</strong>, exclusivamente para fins de avaliação de
                riscos psicossociais conforme a <strong>NR-01</strong> e em conformidade com a
                <strong> Lei Geral de Proteção de Dados (LGPD nº 13.709/2018)</strong>. Os dados
                serão retidos por até <strong>5 anos</strong> e poderão ser excluídos a pedido,
                conforme art. 18 da LGPD.
              </span>
            </label>
          </div>
        )}

        {/* Desktop nav */}
        <div className="hidden sm:flex justify-between mt-8 sm:mt-10">
          <button disabled={currentDim === 0} onClick={() => setCurrentDim((d) => d - 1)}
                  className="btn btn-secondary" data-testid="public-prev-button">
            <i className="fa-solid fa-arrow-left"></i> Anterior
          </button>
          {!isLast ? (
            <button onClick={() => { setCurrentDim((d) => d + 1); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                    className="btn btn-primary" data-testid="public-next-button">
              Próxima <i className="fa-solid fa-arrow-right"></i>
            </button>
          ) : (
            <button disabled={submitting} onClick={submit} className="btn btn-primary" data-testid="public-submit-button">
              {submitting ? <i className="fa-solid fa-circle-notch fa-spin"></i> : <i className="fa-solid fa-paper-plane"></i>}
              {consent ? "Enviar respostas" : "Concluir — marcar termo LGPD"}
            </button>
          )}
        </div>
      </div>

      {/* Mobile sticky nav */}
      <div className="sm:hidden fixed bottom-0 left-0 right-0 z-30 bg-white border-t border-slate-200 px-4 py-3 flex gap-2 shadow-lg">
        <button disabled={currentDim === 0} onClick={() => setCurrentDim((d) => d - 1)}
                className="btn btn-secondary flex-1" data-testid="public-prev-button-mobile">
          <i className="fa-solid fa-arrow-left"></i>
        </button>
        {!isLast ? (
          <button onClick={() => { setCurrentDim((d) => d + 1); window.scrollTo({ top: 0, behavior: "smooth" }); }}
                  className="btn btn-primary flex-[2]" data-testid="public-next-button-mobile">
            Próxima <i className="fa-solid fa-arrow-right"></i>
          </button>
        ) : (
          <button disabled={submitting} onClick={submit}
                  className={`btn flex-[2] ${consent ? "btn-primary" : "bg-amber-500 text-white hover:bg-amber-600"}`}
                  data-testid="public-submit-button-mobile">
            {submitting
              ? <i className="fa-solid fa-circle-notch fa-spin"></i>
              : consent
                ? <><i className="fa-solid fa-paper-plane"></i> Enviar</>
                : <><i className="fa-solid fa-arrow-down"></i> Marcar termo LGPD</>}
          </button>
        )}
      </div>
    </div>
  );
}
